import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, TextField, InputAdornment, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import moment from "moment";
import { useChatScreen } from "../../hooks/useChat";

export default function Chat({ setToggleChat, id }) {
  const { messages, onSend, profile } = useChatScreen(id);
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);
  const myNsId = `customer:${profile?._id}`;

  // Always scroll to the newest message when the list updates.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft("");
  };

  return (
    <Box
      sx={{
        position: "fixed",
        right: { xs: 12, md: 24 },
        bottom: { xs: 12, md: 24 },
        width: { xs: "calc(100vw - 24px)", sm: 380 },
        height: { xs: "70vh", sm: 540 },
        maxHeight: "90vh",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderRadius: 3,
        boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        overflow: "hidden",
        border: "1px solid #e5e7eb",
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5,
        bgcolor: "#000",
        display: "flex", alignItems: "center", gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: "#22c55e", width: 36, height: 36 }}>
          <TwoWheelerIcon sx={{ color: "#000" }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>
            Your rider
          </Typography>
          <Typography sx={{ color: "#9ca3af", fontSize: "0.72rem" }}>
            Online · usually replies instantly
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setToggleChat(false)} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: "#f9fafb",
          px: 1.5, py: 1.5,
          display: "flex", flexDirection: "column", gap: 0.5,
        }}>
        {messages.length === 0 && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ color: "#6b7280", fontSize: "0.85rem", textAlign: "center" }}>
              Send a message to your rider —<br />they'll see it on their phone.
            </Typography>
          </Box>
        )}

        {messages.map((msg, idx) => {
          const mine = msg.user._id === myNsId;
          const prev = messages[idx - 1];
          const showName = !mine && (!prev || prev.user._id !== msg.user._id);
          return (
            <Box
              key={msg._id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: mine ? "flex-end" : "flex-start",
                mt: showName ? 1 : 0.25,
                maxWidth: "85%",
                alignSelf: mine ? "flex-end" : "flex-start",
              }}>
              {showName && (
                <Typography sx={{ fontSize: "0.7rem", color: "#6b7280", mb: 0.25, ml: 1 }}>
                  {msg.user.name}
                </Typography>
              )}
              <Box sx={{
                bgcolor: mine ? "#000" : "#fff",
                color: mine ? "#fff" : "#111",
                px: 1.5, py: 1,
                borderRadius: 2.5,
                borderTopRightRadius: mine ? 0.5 : 2.5,
                borderTopLeftRadius: mine ? 2.5 : 0.5,
                border: mine ? "none" : "1px solid #e5e7eb",
                wordBreak: "break-word",
              }}>
                <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.35 }}>{msg.text}</Typography>
                <Typography sx={{
                  fontSize: "0.65rem",
                  color: mine ? "#9ca3af" : "#9ca3af",
                  mt: 0.25,
                  textAlign: mine ? "right" : "left",
                }}>
                  {moment(msg.createdAt).format("LT")}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Composer */}
      <Box sx={{
        borderTop: "1px solid #e5e7eb",
        px: 1, py: 0.75,
        bgcolor: "#fff",
        display: "flex", alignItems: "flex-end", gap: 0.5,
      }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          variant="standard"
          placeholder="Type a message…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "0.92rem",
              px: 1.5, py: 0.75,
              borderRadius: 99,
              bgcolor: "#f3f4f6",
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!draft.trim()}
          sx={{
            bgcolor: draft.trim() ? "#22c55e" : "#e5e7eb",
            color: draft.trim() ? "#000" : "#9ca3af",
            "&:hover": { bgcolor: draft.trim() ? "#16a34a" : "#e5e7eb" },
            width: 40, height: 40,
          }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
